import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoCrawlState, seoRunCrawl } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/site-health")({
  head: () => ({ meta: [{ title: "Site health · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: SiteHealthPage,
});

function SiteHealthPage() {
  const load = useServerFn(seoCrawlState);
  const run = useServerFn(seoRunCrawl);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-crawl"], queryFn: () => load() });
  const d = q.data as any;

  const crawl = useMutation({
    mutationFn: () =>
      run({ data: { baseUrl: typeof window !== "undefined" ? window.location.origin : undefined, maxPages: 150 } }),
    onSuccess: (r: any) => {
      toast.success(`Checked ${r.pagesCrawled} pages — ${r.broken} broken link(s), ${r.orphans} orphan page(s).`);
      qc.invalidateQueries({ queryKey: ["seo-crawl"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "The scan could not run."),
  });

  const run0 = d?.run;
  const gaps = (d?.coverageGaps ?? []) as any[];
  const broken = (d?.broken ?? []) as any[];

  return (
    <SeoPage
      title="Site health"
      description="A full scan of your own site: every page's status, how deep it sits, how many pages link to it, and every broken link found."
      actions={
        <Button onClick={() => crawl.mutate()} disabled={crawl.isPending}>
          {crawl.isPending ? "Scanning…" : "Scan the site now"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : !run0 ? (
        <Empty>No scan yet — run one to see broken links, orphan pages and link depth.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Pages checked" value={num(run0.pages_crawled ?? 0)} sub={new Date(run0.started_at).toLocaleString()} />
            <Stat label="Broken links" value={num(run0.broken_count ?? 0)} />
            <Stat label="Orphan pages" value={num(run0.orphan_count ?? 0)} />
            <Stat label="Deepest page" value={`${run0.max_depth ?? 0} clicks`} />
          </div>

          <Panel title="Broken links" subtitle="Links that return an error when followed.">
            {broken.length === 0 ? (
              <Empty>No broken links found in this scan.</Empty>
            ) : (
              <DataTable
                head={["On page", "Links to", "Type", "Status"]}
                rows={broken.slice(0, 100).map((b) => [b.source_path, b.target_url, b.kind, String(b.status_code ?? "—")])}
              />
            )}
          </Panel>

          <Panel title="Internal link coverage" subtitle="Pages that few or no other pages link to, or that sit too many clicks from the homepage.">
            {gaps.length === 0 ? (
              <Empty>Every page has healthy internal links.</Empty>
            ) : (
              <DataTable
                head={["Page", "Links in", "Clicks from home", "Severity"]}
                rows={gaps.slice(0, 100).map((g) => [
                  g.path,
                  num(g.inbound),
                  String(g.depth),
                  <Pill key={g.path} tone={g.severity === "critical" ? "bad" : "warn"}>
                    {g.severity}
                  </Pill>,
                ])}
              />
            )}
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
